import React, { useCallback, useEffect, useMemo, useState } from 'react';
import AnimationWrapper from '../../../components/animation/animationWrapper';
import {
  DynamicTable,
  useDynamicTable,
  withDynamicTableContext,
  useTableSchemaLoader,
} from '@wealthlane/table-builder';
import DoneTradeSellItemsHeader from '../../../constants/table-headers/doneTradeSellItemsHeader';
import { axiosInstance } from '../../../services/apiInterceptor';
import { useBoolean, useGetCsDoneTradeSellItems, usePostCsExecutedDoneTrade } from '../../../hooks';
import type { Wealthlane_AwardSettlementService_Dtos_BrokerTradeTool_FillTradeOrderDto } from '@wealthlane/api-services/wealthlane-award-settlement-service';
import { useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import ToastComponent from '../../../components/toast';
import DoneTradeModal from '../broker-trade-tool/doneTradeModal';
import {
  Wealthlane_AwardService_Dtos_BrokerTradeTool_CsSellItemDto,
  Wealthlane_AwardService_Dtos_BrokerTradeTool_CsDoneTradePreviewDto,
  Wealthlane_AwardService_Dtos_BrokerTradeTool_CreateExecutedDoneTradeDto,
} from '@wealthlane/api-services/wealthlane-award-service';
import localSellSchema from './schemas/CS_DONE_TRADE_SELL_ITEMS_TABLE.json';

interface RowState {
  sellShares: number;
  error: string;
}

interface PendingDoneTradeAction {
  item: Wealthlane_AwardService_Dtos_BrokerTradeTool_CsSellItemDto;
  sellShares: number;
}

interface DoneTradeSellShareSectionProps {
  participantId: string | undefined;
  companyId: string | undefined;
  participant: any | null;
  reasonCode: string;
  awardHandleRequest: (promise: any) => Promise<any>;
  orderStatusOptions: any;
  isOrderStatusLoading: boolean;
  canFillDT: boolean;
}

function DoneTradeSellShareSection({
  participantId,
  companyId,
  participant,
  reasonCode,
  awardHandleRequest,
  orderStatusOptions,
  isOrderStatusLoading,
  canFillDT,
}: DoneTradeSellShareSectionProps) {
  const queryClient = useQueryClient();
  const { tableInfo } = useDynamicTable();

  const { schemaData: remoteSchemaData, status: schemaStatus } = useTableSchemaLoader({
    identifier: 'CS_DONE_TRADE_SELL_ITEMS_TABLE',
  });

  const hasRemoteSchema = schemaStatus === 'success' && remoteSchemaData?.columns?.length > 0;
  const sellSchemaData = useMemo(() => {
    const source = hasRemoteSchema ? remoteSchemaData : localSellSchema;
    return {
      ...source,
      columns: (source?.columns || []).filter(
        (col: { accessor?: string }) => col.accessor !== 'recordTypeName'
      ),
    };
  }, [hasRemoteSchema, remoteSchemaData]);

  const [rowStates, setRowStates] = useState<Record<string, RowState>>({});
  const [doneTradePreview, setDoneTradePreview] =
    useState<Wealthlane_AwardService_Dtos_BrokerTradeTool_CsDoneTradePreviewDto | null>(null);
  const [pendingAction, setPendingAction] = useState<PendingDoneTradeAction | null>(null);
  const [submittingItemId, setSubmittingItemId] = useState<string | null>(null);

  const {
    value: isDoneTradeOpen,
    setFalse: closeDoneTrade,
    setTrue: openDoneTrade,
  } = useBoolean(false);

  const { mutateAsync: createExecutedDoneTrade, isLoading: isSubmitting } =
    usePostCsExecutedDoneTrade(awardHandleRequest);

  const sellSkipCount = tableInfo?.skipCount ?? 0;
  const sellMaxResult = tableInfo?.maxResultCount ?? 10;

  const sellFilters = useMemo(
    () => ({
      awardType: tableInfo?.awardType,
    }),
    [tableInfo?.awardType]
  );

  const { data: sellItemsData, isLoading: isSellItemsLoading } = useGetCsDoneTradeSellItems(
    awardHandleRequest,
    participantId,
    companyId,
    sellSkipCount,
    sellMaxResult,
    sellFilters
  );
  const sellItems = sellItemsData?.items;
  const sellTotalCount = sellItemsData?.totalCount ?? 0;

  useEffect(() => {
    if (sellItems) {
      const initial: Record<string, RowState> = {};
      sellItems.forEach((item) => {
        if (item.itemId && !rowStates[item.itemId]) {
          initial[item.itemId] = {
            sellShares: 0,
            error: '',
          };
        }
      });
      if (Object.keys(initial).length > 0) {
        setRowStates((prev) => ({ ...initial, ...prev }));
      }
    }
  }, [sellItems]);

  const validateRow = useCallback(
    (item: Wealthlane_AwardService_Dtos_BrokerTradeTool_CsSellItemDto, row: RowState): string => {
      if (row.sellShares <= 0) return 'Enter shares to sell';
      if (row.sellShares > (item.maxShares ?? 0)) return `Cannot exceed ${item.maxShares} shares`;
      return '';
    },
    []
  );

  const buildPreview = useCallback(
    (
      item: Wealthlane_AwardService_Dtos_BrokerTradeTool_CsSellItemDto,
      sellShares: number
    ): Wealthlane_AwardService_Dtos_BrokerTradeTool_CsDoneTradePreviewDto => ({
      tradeOrderId: '',
      transactionId: '',
      createdDate: new Date().toISOString(),
      orderType: 'Market',
      company: participant?.companyName ?? '',
      ticker: participant?.ticker ?? '',
      shares: sellShares,
      brokerSellType: 'Shr',
      brokerName: participant?.brokerName ?? '',
      brokerAccount: participant?.brokerAccountNumber ?? '',
      orderStatus: 'Filled',
      awardType: item.awardType,
      recordType: item.recordTypeName ?? item.itemType,
    }),
    [participant]
  );

  const resolveActionItem = useCallback(
    (rowData: Wealthlane_AwardService_Dtos_BrokerTradeTool_CsSellItemDto) => {
      if (!rowData.itemId) return null;
      const item = sellItems?.find((i) => i.itemId === rowData.itemId) ?? rowData;
      const row = rowStates[rowData.itemId];
      if (!row) return null;

      const error = validateRow(item, row);
      if (error !== '') {
        setRowStates((prev) => ({
          ...prev,
          [rowData.itemId!]: { ...prev[rowData.itemId!], error },
        }));
        return null;
      }

      return { item, sellShares: row.sellShares };
    },
    [sellItems, rowStates, validateRow]
  );

  const openFillModal = useCallback(
    (action: PendingDoneTradeAction) => {
      setPendingAction(action);
      setDoneTradePreview(buildPreview(action.item, action.sellShares));
      setSubmittingItemId(action.item.itemId ?? null);
      openDoneTrade();
    },
    [buildPreview, openDoneTrade]
  );

  const handleEnterFill = useCallback(
    (rowData: Wealthlane_AwardService_Dtos_BrokerTradeTool_CsSellItemDto) => {
      const action = resolveActionItem(rowData);
      if (!action) return;
      openFillModal(action);
    },
    [resolveActionItem, openFillModal]
  );

  const handleDoneTradeSubmit = useCallback(
    async (fillData: Wealthlane_AwardSettlementService_Dtos_BrokerTradeTool_FillTradeOrderDto) => {
      if (!pendingAction?.item.itemId || !participant?.participantId) return;

      const itemId = pendingAction.item.itemId;
      setSubmittingItemId(itemId);
      try {
        const payload: Wealthlane_AwardService_Dtos_BrokerTradeTool_CreateExecutedDoneTradeDto = {
          participantId: participant.participantId,
          companyId: companyId || undefined,
          items: [
            {
              openLotId: itemId,
              sellShares: fillData.soldShares ?? pendingAction.sellShares,
            },
          ],
          salePrice: fillData.salePrice,
          fee: fillData.fee,
          commission: fillData.commission,
          cashProceeds: fillData.cashProceeds,
          orderExecutedDate: fillData.orderExecutedDate,
          orderNumber: fillData.orderNumber,
          reasonCode: reasonCode || undefined,
        };

        await createExecutedDoneTrade(payload);

        closeDoneTrade();
        setDoneTradePreview(null);
        setPendingAction(null);
        setRowStates((prev) => ({
          ...prev,
          [itemId]: { sellShares: 0, error: '' },
        }));
      } catch (err: any) {
        const msg = err?.body?.error?.message || err?.message || '';
        if (msg.includes('STALE_BALANCE')) {
          toast.error(
            <ToastComponent
              label='Error'
              message='Balance has changed. Refreshing available shares...'
            />
          );
          void queryClient.invalidateQueries('getCsDoneTradeSellItems');
        }
      } finally {
        setSubmittingItemId(null);
      }
    },
    [
      pendingAction,
      participant,
      companyId,
      reasonCode,
      createExecutedDoneTrade,
      closeDoneTrade,
      queryClient,
    ]
  );

  const updateRowState = useCallback((itemId: string, field: string, value: string | number) => {
    setRowStates((prev) => ({
      ...prev,
      [itemId]: { ...prev[itemId], [field]: value, error: '' },
    }));
  }, []);

  const tableData = useMemo(() => {
    if (!sellItems) return [];
    return sellItems.map((item) => {
      const row = item.itemId ? rowStates[item.itemId] : undefined;
      const sellShares = row?.sellShares ?? 0;
      return {
        ...item,
        sellShares,
        bookingType: 'DoneTrade',
        estimatedValue: sellShares * (item.referenceMarketPrice ?? 0),
        rowError: row?.error ?? '',
      };
    });
  }, [sellItems, rowStates]);

  const sellSchema = useMemo(
    () => ({
      columns: DoneTradeSellItemsHeader({
        actions: sellSchemaData?.columns || [],
        onEnterFill: (rowData: any) => handleEnterFill(rowData),
        onUpdateRow: updateRowState,
        submittingItemId,
        canFillDT,
      }),
      options: sellSchemaData?.options as any,
    }),
    [sellSchemaData, handleEnterFill, updateRowState, submittingItemId, canFillDT]
  );

  const resetModalState = useCallback(() => {
    closeDoneTrade();
    setDoneTradePreview(null);
    setPendingAction(null);
    setSubmittingItemId(null);
  }, [closeDoneTrade]);

  return (
    <>
      <DynamicTable
        id='doneTradeSellItemsTable'
        schema={sellSchema}
        data={tableData}
        totalCounts={sellTotalCount || 10}
        loading={isSellItemsLoading ? 'loading' : 'success'}
        showPagination={sellTotalCount > 0}
        showPaginationList={sellTotalCount > sellMaxResult}
        components={{}}
      />

      <AnimationWrapper>
        {isDoneTradeOpen && (
          <DoneTradeModal
            isOpen={isDoneTradeOpen}
            onClose={resetModalState}
            previewData={doneTradePreview}
            onSubmit={handleDoneTradeSubmit}
            isSubmitting={isSubmitting}
            orderStatusOptions={orderStatusOptions}
            isOrderStatusLoading={isOrderStatusLoading}
            flowMode='done-trade'
            hideRecordType
            disableOrderStatus
          />
        )}
      </AnimationWrapper>
    </>
  );
}

export default withDynamicTableContext(DoneTradeSellShareSection, true, axiosInstance);
